import { h, Component } from 'preact';
import util from 'preact-util';
import { observer } from 'mobx-preact';

import localUtil from '../../lib/util';
import fields from '../../lib/fields';
import FormFields from '../../components/form/fields';
import { appAlert } from '../../lib/dialogs';

@observer
class CoffeeView extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isExtracting: false,
            showNormalizations: false,
            sections: fields.get('coffee'),
        };
    }

    componentDidMount() {
        const { id, isNew } = this.props;
        if (id && !isNew) {
            this.loadCoffee(id);
        }
    }

    loadCoffee = async (id) => {
        const { coffeeStore } = this.props.stores;
        await coffeeStore.load(id);
    }

    saveCoffee = async () => {
        const { coffeeStore, appState } = this.props.stores;
        const { isNew, callback } = this.props;

        if (isNew) {
            // Use insert for new items (POST)
            await coffeeStore.insert(coffeeStore.newCoffee);
            coffeeStore.updateKeyValue('newCoffee', {});
        } else {
            // Use save for existing items (PATCH)
            await coffeeStore.save(coffeeStore.coffee);
        }

        if (callback) {
            callback();
        }
        appState.toggleDrawer();
    }

    extractFromImage = async () => {
        const { coffeeStore } = this.props.stores;
        const { isNew, id } = this.props;

        // Get the current coffee object
        const coffee = isNew ? coffeeStore.newCoffee : coffeeStore.coffee;
        const coffeeId = coffee.id || id;
        const images = coffee.images || [];

        if (images.length === 0) {
            (await appAlert('Please upload an image first'));
            return;
        }

        // Use the first image's URL (prefer larger versions for better extraction)
        const image = images[0];
        const imageUrl = image.s3LargeLink || image.s3MediumLink || image.s3Link;

        if (!imageUrl) {
            (await appAlert('No image URL found'));
            return;
        }

        this.setState({ isExtracting: true });

        try {
            // Pass coffeeId to save directly to database
            const result = await coffeeStore.extractFromImageUrl(imageUrl, coffeeId);

            if (result && result.coffee) {
                // If saved to database, reload the coffee
                if (result.saved && coffeeId) {
                    await coffeeStore.load(coffeeId);
                } else {
                    // Merge extracted data with the current coffee object
                    const objectName = isNew ? 'newCoffee' : 'coffee';
                    const currentData = coffeeStore[objectName] || {};
                    const mergedData = {
                        ...currentData,
                        ...result.coffee,
                        // Keep images from current data
                        images: currentData.images,
                    };
                    coffeeStore.updateKeyValue(objectName, mergedData);
                }

                // Show normalizations if any were applied
                if (result.normalizations && result.normalizations.length > 0) {
                    this.setState({ showNormalizations: true });
                }
            }
        } catch (err) {
            console.error('Extraction failed:', err);
            (await appAlert('Failed to extract data from image. Please try again.'));
        } finally {
            this.setState({ isExtracting: false });
        }
    }

    render() {
        const { coffeeStore } = this.props.stores;
        const { isNew, drawerLevel = 1 } = this.props;
        const { sections, isExtracting, showNormalizations } = this.state;

        const objectName = isNew ? 'newCoffee' : 'coffee';
        const coffee = coffeeStore[objectName] || {};
        const images = coffee.images || [];
        const hasImages = images.length > 0;

        // Get normalizations from store
        const { normalizations = [] } = coffeeStore;

        return (
            <div class='coffee-view'>
                <div class='w-100 d-flex justify-content-center'>
                    <div class='w-100' style='max-width: 960px; padding-top: 20px; padding-bottom: 100px;'>

                        <div class='font-weight-lighter px-3 box-header mt-0'>
                            {isNew ? 'New Coffee' : 'Edit Coffee'}
                        </div>

                        {/* AI Extraction Button */}
                        {hasImages && (
                            <div class='px-3 mb-3'>
                                <div class='card bg-info bg-opacity-10 border-info'>
                                    <div class='card-body py-2'>
                                        <div class='d-flex align-items-center justify-content-between'>
                                            <div>
                                                <i class='fa-solid fa-wand-magic-sparkles me-2 text-info' />
                                                <span class='text-info'>Extract data from coffee card image</span>
                                            </div>
                                            <button
                                                class='btn btn-info btn-sm rounded-pill'
                                                onClick={this.extractFromImage}
                                                disabled={isExtracting}
                                            >
                                                {isExtracting ? (
                                                    <>
                                                        <i class='fa-solid fa-spinner fa-spin me-1' />
                                                        Extracting...
                                                    </>
                                                ) : (
                                                    <>
                                                        <i class='fa-solid fa-robot me-1' />
                                                        Extract with AI
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Normalizations notification */}
                        {showNormalizations && normalizations.length > 0 && (
                            <div class='px-3 mb-3'>
                                <div class='alert alert-success alert-dismissible fade show' role='alert'>
                                    <strong>
                                        <i class='fa-solid fa-check-circle me-1' />
                                        Data extracted and normalized!
                                    </strong>
                                    <p class='mb-1 mt-2'>The following fields were matched to existing values:</p>
                                    <ul class='mb-0'>
                                        {normalizations.map(n => (
                                            <li key={n.field}>
                                                <strong>{n.field}:</strong> "{n.original}" → "{n.normalized}" ({n.similarity}% match)
                                            </li>
                                        ))}
                                    </ul>
                                    <button
                                        type='button'
                                        class='btn-close'
                                        onClick={() => this.setState({ showNormalizations: false })}
                                    />
                                </div>
                            </div>
                        )}

                        <div class='w-100 d-flex flex-column justify-content-start'>
                            <div class='bg-light d-flex flex-column justify-content-start h-100 pt-3'>
                                <FormFields
                                    stores={this.props.stores}
                                    sections={sections}
                                    holdingStore={coffeeStore}
                                    objectName={objectName}
                                    isNew={isNew}
                                    drawerLevel={drawerLevel}
                                />
                            </div>
                        </div>
                    </div>
                </div>

                {/* Action buttons - only show for new coffees */}
                {isNew && (
                    <div class='w-100 d-flex flex-column justify-content-start mt-3 px-4 py-3 bg-light fixed-bottom' style='bottom: 0; left: 0;'>
                        <div class='d-flex justify-content-end'>
                            <button
                                class='btn btn-primary rounded-pill'
                                onClick={this.saveCoffee}
                            >
                                <i class='fa-solid fa-save me-1' />
                                Create Coffee
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    }
}

export default CoffeeView;
